import React, { useState, useEffect } from "react";
import classes from './AddCustomerModal.module.css';
import api, { withCompanyId } from "../API/Api";
import Swal from 'sweetalert2';

const AddCustomerModal = ({ isOpen, onClose, onSuccess, initialName = "", editingCustomer = null }) => {
    const [submitting, setSubmitting] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    
    const [formData, setFormData] = useState({
        type: '', // 'individual' or 'corporate'
        nin: '', // National Identification Number
        rn: '', // Registration Number (CAC)
        name: '',
        email: '',
        phone: '',
        address: '',
        office_address: '',
        credit_limit: '' // Credit Limit
    });

    const isEditMode = !!editingCustomer;

    useEffect(() => {
        if (isOpen) {
            if (editingCustomer) {
                // Edit mode: populate form with customer data
                setFormData({
                    type: editingCustomer.type || '',
                    nin: editingCustomer.nin || '',
                    rn: editingCustomer.rn || '',
                    name: editingCustomer.name || '',
                    email: editingCustomer.email || '',
                    phone: editingCustomer.phone || '',
                    address: editingCustomer.address || '',
                    office_address: editingCustomer.office_address || '',
                    credit_limit: editingCustomer.credit_limit || ''
                });
                setIsValidated(true); // Don't require re-validation in edit mode
            } else {
                // Add mode: reset form
                setFormData(prev => ({ ...prev, name: initialName }));
                setIsValidated(false);
            }
            setValidationError("");
        }
    }, [isOpen, initialName, editingCustomer]);

    // Auto-validate NIN when it reaches 11 digits
    // useEffect(() => {
    //     if (formData.type === 'individual' && formData.nin.length === 11 && !isValidated) {
    //         validateNIN(formData.nin);
    //     }
    // }, [formData.nin]);

    // Auto-validate RN when it reaches 5+ characters
    // useEffect(() => {
    //     if (formData.type === 'corporate' && formData.rn.length >= 5 && !isValidated) {
    //         validateRN(formData.rn);
    //     }
    // }, [formData.rn]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Helper function to format error messages (handles both string and object errors)
    const formatErrorMessage = (message) => {
        if (typeof message === 'string') {
            return message;
        }
        if (typeof message === 'object' && message !== null) {
            return Object.entries(message)
                .map(([key, errors]) => {
                    if (Array.isArray(errors)) {
                        return errors.join(' ');
                    }
                    return String(errors);
                })
                .join('\n');
        }
        return 'An error occurred';
    };

    const validateNIN = async (nin) => {
        if (!nin || nin.trim().length === 0) {
            setValidationError("NIN is required");
            return false;
        }

        // Basic NIN format validation (11 digits for Nigeria)
        const ninRegex = /^\d{11}$/;
        if (!ninRegex.test(nin.trim())) {
            setValidationError("NIN must be 11 digits");
            return false;
        }

        // Extract first_name and last_name from customer name
        const nameParts = (formData.name || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        if (!firstName || !lastName) {
            setValidationError("Please enter both first name and last name in the Customer Name field (e.g., 'John Doe')");
            return false;
        }

        setValidating(true);
        setValidationError("");

        try {
            // Include NIN, first_name, and last_name in the validation request
            const params = new URLSearchParams({
                nin: nin.trim(),
                first_name: firstName,
                last_name: lastName
            });
            const response = await api.get(`/v1/verify-nin?${params.toString()}`, withCompanyId({}));
            
            if (response.data.success && response.data.data) {
                // Auto-fill fields with validated data
                const userData = response.data.data;
                
                setFormData(prev => {
                    // Combine first_name and last_name if they exist separately
                    let fullName = userData.name || prev.name;
                    if (!fullName && (userData.first_name || userData.last_name)) {
                        fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                    }
                    
                    return {
                        ...prev,
                        name: fullName,
                        email: userData.email || prev.email,
                        phone: userData.phone || userData.phone_number || prev.phone,
                        address: userData.address || userData.residential_address || prev.address,
                    };
                });
                setIsValidated(true);
                setValidationError("");
                return true;
            } else {
                setValidationError(formatErrorMessage(response.data.message) || "NIN validation failed");
                setIsValidated(false);
                return false;
            }
        } catch (error) {
            console.log("NIN Validation Error:", error);
            setValidationError(formatErrorMessage(error.response?.data?.message) || "Failed to validate NIN. Please try again.");
            setIsValidated(false);
            return false;
        } finally {
            setValidating(false);
        }
    };

    const validateRN = async (rn) => {
        if (!rn || rn.trim().length === 0) {
            setValidationError("RN (CAC Registration Number) is required");
            return false;
        }

        // Basic RN format validation (CAC numbers are typically alphanumeric)
        const rnRegex = /^[A-Z0-9]{5,}$/;
        if (!rnRegex.test(rn.trim())) {
            setValidationError("Please enter a valid CAC Registration Number");
            return false;
        }

        setValidating(true);
        setValidationError("");

        try {
            // Connect this to your actual CAC/RN validation API
            const params = new URLSearchParams({
                cac_number: rn.trim()
            });
            const response = await api.get(`/v1/beneficiary/verify-cac?${params.toString()}`, withCompanyId({}));
            
            if (response.data.status && response.data.data) {
                // Auto-fill fields with validated data from database
                const companyData = response.data.data;
                
                console.log("CAC Validation Response:", companyData);
                
                setFormData(prev => {
                    // Map API response fields to form fields
                    const updatedData = {
                        ...prev,
                        name: (companyData.name || '').trim() || prev.name,
                        email: (companyData.email || '').trim() || prev.email,
                        phone: (companyData.phone || '').trim() || prev.phone,
                        address: (companyData.address || '').trim() || prev.address,
                    };
                    
                    console.log("Updated Form Data:", updatedData);
                    return updatedData;
                });
                setIsValidated(true);
                setValidationError("");
                return true;
            } else {
                setValidationError(formatErrorMessage(response.data.message) || "CAC validation failed");
                setIsValidated(false);
                return false;
            }
        } catch (error) {
            console.log("CAC Validation Error:", error);
            setValidationError(formatErrorMessage(error.response?.data?.message) || "Failed to validate CAC number. Please try again.");
            setIsValidated(false);
            return false;
        } finally {
            setValidating(false);
        }
    };

    const handleCustomerTypeChange = (e) => {
        const selectedType = e.target.value;
        setFormData(prev => ({
            ...prev,
            type: selectedType,
            nin: '',
            rn: ''
        }));
        setIsValidated(false);
        setValidationError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitting(true);

        try {
            const payload = {
                type: formData.type,
                nin: formData.type === 'individual' ? formData.nin : null,
                rn: formData.type === 'corporate' ? formData.rn : null,
                name: formData.name,
                address: formData.address,
                office_address: formData.office_address,
            };

            // Only include email if it has a value
            if (formData.email && formData.email.trim()) {
                payload.email = formData.email;
            }

            // Only include phone if it has a value
            if (formData.phone && formData.phone.trim()) {
                payload.phone = formData.phone;
            }

            // Only include credit_limit if it has a value
            if (formData.credit_limit && formData.credit_limit.trim()) {
                payload.credit_limit = formData.credit_limit;
            }

            console.log("Sending Payload:", payload);

            const body = withCompanyId(payload);
            let response;
            let isUpdate = false;

            if (isEditMode) {
                // Update existing customer
                response = await api.post(`/v1/customer/update`, { ...body, id: editingCustomer.id });
                isUpdate = true;
            } else {
                // Create new customer
                response = await api.post(`/v1/customer/add`, body);
            }

            // Reset form
            setFormData({
                type: '',
                nin: '',
                rn: '',
                name: "",
                email: "",
                phone: "",
                address: "",
                office_address: "",
                credit_limit: "",
            });
            setIsValidated(false);
            setValidationError("");

            Swal.fire({
                icon: "success",
                title: isUpdate ? "Customer Updated" : "Customer Added",
                text: response.data.message || (isUpdate ? 'Customer updated!' : 'Customer added!'),
                timer: 2000,
                showConfirmButton: false,
                didOpen: () => {
                    const swalContainer = document.querySelector('.swal2-container');
                    if (swalContainer) {
                        swalContainer.style.zIndex = '100000';
                    }
                }
            });

            // Callback with new/updated customer data
            if (onSuccess) {
                onSuccess(response.data.data);
            }

            onClose();

        } catch (error) {
            console.log("❌ Error Keys:", Object.keys(error.response?.data || {}));

            const errorMessage = formatErrorMessage(error.response?.data?.message) || "Something went wrong.";

            Swal.fire({
                icon: "error",
                title: isEditMode ? "Failed to Update Customer" : "Failed to Add Customer",
                text: errorMessage,
                didOpen: () => {
                    const swalContainer = document.querySelector('.swal2-container');
                    if (swalContainer) {
                        swalContainer.style.zIndex = '100000';
                    }
                }
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={classes.modalBackdrop}>
            <div className={classes.modal}>
                <h2>{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h2>

                <div className={classes.scrollContainer}>
                    <form className={classes.modalForm} onSubmit={handleSubmit}>
                        {/* Customer Type Selection and Credit Limit */}
                        <div className={classes.inputRow}>
                            <div className={classes.inputGroup}>
                                <label>Customer Type <span style={{ color: 'red' }}>*</span></label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleCustomerTypeChange}
                                    required
                                    disabled={isValidated}
                                    style={{
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        fontFamily: 'inherit',
                                        cursor: isValidated ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <option value="">Select Customer Type</option>
                                    <option value="individual">Individual</option>
                                    <option value="corporate">Corporate</option>
                                </select>
                            </div>
                            <div className={classes.inputGroup}>
                                <label>Credit Limit</label>
                                <input
                                    type="number"
                                    name="credit_limit"
                                    placeholder="Enter Credit Limit Amount"
                                    value={formData.credit_limit}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        {/* Customer Name Field */}
                        {formData.type && (
                            <div className={classes.inputGroup}>
                                <label>
                                    Customer Name 
                                    {formData.type === 'individual' && <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>(e.g., John Doe)</span>}
                                    <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder={formData.type === 'individual' ? "Enter full name (First Last)" : "Enter company name"}
                                    value={formData.name}
                                    onChange={handleChange}
                                    readOnly={isValidated}
                                    style={{
                                        backgroundColor: isValidated ? '#f0f0f0' : 'white',
                                        opacity: isValidated ? '0.8' : '1',
                                        cursor: isValidated ? 'not-allowed' : 'auto'
                                    }}
                                    required
                                />
                            </div>
                        )}

                        {/* NIN Field for Individual */}
                        {formData.type === 'individual' && (
                            <div className={classes.inputGroup}>
                                <label>National Identification Number (NIN) <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    name="nin"
                                    placeholder="Enter 11-digit NIN"
                                    value={formData.nin}
                                    onChange={handleChange}
                                    disabled={isValidated}
                                    maxLength="11"
                                    style={{
                                        opacity: isValidated ? '0.6' : '1'
                                    }}
                                />
                                {validating && <div style={{ fontSize: '12px', color: '#007bff', marginTop: '5px' }}>Validating...</div>}
                            </div>
                        )}

                        {/* RN Field for Corporate */}
                        {formData.type === 'corporate' && (
                            <div className={classes.inputGroup}>
                                <label>CAC Registration Number (RN) <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    name="rn"
                                    placeholder="Enter CAC Registration Number"
                                    value={formData.rn}
                                    onChange={handleChange}
                                    disabled={isValidated}
                                    style={{
                                        opacity: isValidated ? '0.6' : '1'
                                    }}
                                />
                                {validating && <div style={{ fontSize: '12px', color: '#007bff', marginTop: '5px' }}>Validating...</div>}
                            </div>
                        )}

                        {/* Validation Error Message */}
                        {validationError && (
                            <div style={{
                                padding: '10px',
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                borderRadius: '4px',
                                marginBottom: '15px',
                                fontSize: '14px',
                                border: '1px solid #f5c6cb'
                            }}>
                                {validationError}
                            </div>
                        )}

                        {/* Validation Success Message */}
                        {isValidated && (
                            <div style={{
                                padding: '10px',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                borderRadius: '4px',
                                marginBottom: '15px',
                                fontSize: '14px',
                                border: '1px solid #c3e6cb'
                            }}>
                                ✓ {formData.type === 'individual' ? 'NIN' : 'CAC'} Validated Successfully. Other fields have been auto-filled.
                            </div>
                        )}

                        <div className={classes.inputRow}>
                            <div className={classes.inputGroup}>
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Enter Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    // required
                                />
                            </div>
                            <div className={classes.inputGroup}>
                                <label>Phone Number</label>
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder="Enter Phone Number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    // required
                                />
                            </div>
                        </div>

                        <div className={classes.inputRow}>
                            <div className={classes.inputGroup}>
                                <label>Customer's Address</label>
                                <input
                                    type="text"
                                    name="address"
                                    placeholder="Enter Customer's Address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={classes.inputGroup}>
                                <label>Customer's Office Address</label>
                                <input
                                    type="text"
                                    name="office_address"
                                    placeholder="Enter Customer's Office Address"
                                    value={formData.office_address}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className={classes.modalActions}>
                            <button type="submit" className={classes.btnss} disabled={submitting}>
                                {submitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Customer' : 'Add Customer')}
                            </button>
                            <button
                                type="button"
                                className={classes.btnssWhite}
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddCustomerModal;
